import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Star, CheckCircle, Thermometer, Droplet, Coffee, Flame, Cookie, ChevronLeft, ChevronRight } from 'lucide-react';

// Import local images
import newCupImage from '../assets/images/new_cup.jpg';
import cup220Image from '../assets/images/cup220_1.jpg';

// Import carousel images
import cup110_1 from '../assets/images/cup110_1.jpg';
import cup110_2 from '../assets/images/cup110_2.jpg';
import cup220_2 from '../assets/images/cup220_2.jpg';
import cup220_3 from '../assets/images/cup220_3.jpg';
import edibleCups from '../assets/images/edible_cups.jpg';
import heroCups from '../assets/images/hero_cups.jpg';
import c5 from '../assets/images/c5.jpg';
import c9 from '../assets/images/c9.jpg';

const EdibleCupsCategory = () => {
  // Carousel state and refs
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Carousel images - 6 edible cup images
  const carouselImages = [
   // { src: cup110_1, alt: 'Cupffee 110ml', title: 'Cupffee 110ml' },
    //{ src: cup110_2, alt: 'Cupffee 110ml Design', title: 'Cupffee 110ml Design' },
    //{ src: cup220_2, alt: 'Cupffee 220ml', title: 'Cupffee 220ml' },
    //{ src: cup220_3, alt: 'Cupffee 220ml Style', title: 'Cupffee 220ml Style' },
    { src: edibleCups, alt: 'Edible Cups Collection', title: 'Edible Cups Collection' },
    //{ src: heroCups, alt: 'Premium Edible Cups', title: 'Premium Edible Cups' },
   { src: c5, alt: 'Premium Edible Cups', title: 'Premium Edible Cups' },
  { src: c9, alt: 'Premium Edible Cups', title: 'Premium Edible Cups' }
  ];

  const totalSlides = carouselImages.length;

  // Go to next slide
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  // Go to previous slide
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Go to specific slide
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isPaused) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 3500);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, nextSlide]);

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };
  // Product data for the two cups - separate pages
  const products = [
    {
      id: 'cup-110ml',
      name: 'Edible CUP | 110 ml',
      cartonPrice: 8,
      cupsPerCarton: 10,
      stock: 'In Stock',
      image: newCupImage,
      description: 'Perfect for espresso and small beverages',
      link: '/edible-cup-110'
    },
    {
      id: 'cup-220ml',
      name: 'Edible CUP | 220 ml',
      cartonPrice: 12,
      cupsPerCarton: 12,
      stock: 'In Stock',
      image: cup220Image,
      description: 'Ideal for coffee, tea and medium drinks',
      link: '/edible-cup-220'
    }
  ];

  return (
    <div className="category-page edible-cups-category">
      {/* Centered Header */}
      <section className="edible-cups-hero">
        <div className="container">
          <h1 className="edible-cups-title">Cupffee Edible Cups</h1>
          <p className="edible-cups-subtitle">
            Discover our range of 100% edible, eco-friendly cups. Made from natural ingredients, 
            perfect for hot and cold beverages.
          </p>
        </div>
      </section>

      {/* Product Images Section */}
      <section className="edible-cups-products">
        <div className="container">
          <div className="edible-cups-grid">
            {products.map(product => (
              <Link to={product.link} key={product.id} className="edible-cup-item">
                <div className="edible-cup-image-wrapper">
                  <img src={product.image} alt={product.name} />
                  <div className="edible-cup-overlay">
                    <span className="view-product-btn">
                      View Product <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
                <div className="edible-cup-info">
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Video Section */}
      <section className="faq-video-section">
        <div className="container">
          <div className="faq-video-header">
            <h2 className="heading-1">Frequently Asked Questions</h2>
            <p className="body-large">Everything you need to know about our Pour Edible Cup</p>
          </div>
          <div className="faq-video-container">
            <video 
              className="faq-video"
              controls
              autoPlay
              muted
              loop
              playsInline
              poster=""
            >
              <source 
                src="/assets/videos/you_asked_we_answered.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Informational Section - Text Boxes */}
      <section className="category-info-section">
        <div className="container">
          <div className="cupffee-text-boxes">
            {/* Row 1 */}
            <div className="text-boxes-row">
              <div className="info-text-box">
                <div className="text-box-icon">
                  <Leaf size={28} />
                </div>
                <h4>100% vegan</h4>
                <p>Handpicked for purity: only the best plant-based ingredients, including natural grains and coconut oil.</p>
              </div>
              <div className="info-text-box">
                <div className="text-box-icon">
                  <Cookie size={28} />
                </div>
                <h4>Crunchy and delicious</h4>
                <p>The sustainable alternative to disposable cups, a tasty solution to plastic pollution!</p>
              </div>
            </div>
            
            {/* Row 2 */}
            <div className="text-boxes-row">
              <div className="info-text-box">
                <div className="text-box-icon">
                  <CheckCircle size={28} />
                </div>
                <h4>GMO free</h4>
                <p>Just nature's best 100% GMO-free no artificial colorants, sweeteners or preservatives</p>
              </div>
              <div className="info-text-box">
                <div className="text-box-icon">
                  <Star size={28} />
                </div>
                <h4>Completely natural</h4>
                <p>Masterfully crafted from 7 sustainable, locally sourced ingredients of natural origin.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Edible Cup Carousel Section */}
      <section className="edible-cup-carousel-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Explore Our Edible Cups</h2>
            <p className="body-large">
              Discover our innovative sustainable cup collection
            </p>
          </div>

          <div 
            className="edible-cup-carousel"
            ref={carouselRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Previous Button */}
            <button 
              className="carousel-btn carousel-btn-prev" 
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Slides Container */}
            <div className="carousel-slides-container">
              <div 
                className="carousel-slides"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselImages.map((image, index) => (
                  <div key={index} className="carousel-slide">
                    <img src={image.src} alt={image.alt} />
                    <div className="carousel-slide-overlay">
                      <span className="carousel-slide-title">{image.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button 
              className="carousel-btn carousel-btn-next" 
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <ChevronRight size={28} />
            </button>

            {/* Dots Indicator */}
            <div className="carousel-dots">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section with Feature Boxes */}
      <section className="why-choose-section">
        <div className="container">
          <h2 className="section-title">Why Choose Our Edible Cup?</h2>
          
          <div className="features-layout">
            {/* Row 1 */}
            <div className="features-row">
              <div className="feature-box">
                <div className="feature-icon"><Thermometer size={40} /></div>
                <h3>Thermo Resistant</h3>
                <p>Cupffee resists high temps up to 85°C, making sure your hot drink stays contained.</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon"><Droplet size={40} /></div>
                <h3>Leak-Proof</h3>
                <p>Thanks to our unique recipe, Cupffee cups won't leak for hours. No leaks, no worries!</p>
              </div>
            </div>

            {/* Row 2 (centered single) */}
            <div className="features-row center-row">
              <div className="feature-box">
                <div className="feature-icon"><Coffee size={40} /></div>
                <h3>No Taste Change</h3>
                <p>Not a fan of sugar? Cupffee won't change the taste of your coffee or tea.</p>
              </div>
            </div>

            {/* Row 3 */}
            <div className="features-row">
              <div className="feature-box">
                <div className="feature-icon"><Flame size={40} /></div>
                <h3>Low Calorie</h3>
                <p>Grab your coffee in a Cupffee for a guilt-free indulgence. Sip and snack away!</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon"><Cookie size={40} /></div>
                <h3>Crunchy</h3>
                <p>Tasty as a cookie, Cupffee will be crunchy from the first sip till the last bite.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EdibleCupsCategory;
